import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Check,
  CheckCheck,
  FileText,
  GraduationCap,
  Package,
  Paperclip,
  Send,
  X,
} from 'lucide-react';
import { Button, EmptyState, ErrorState, Skeleton, StatusBadge } from '@/design/components';
import { cn } from '@/lib/cn';
import { useMe } from '@/auth/useMe';
import { usePerm } from '@/access/usePerm';
import { formatDate, formatTime } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { formatBytes, isImage, signedUrls, uploadAttachment, type Attachment } from './attachments';
import {
  useChannel,
  useChannelRealtime,
  useMessageMutations,
  useMessages,
  type ChannelMessage,
  useOnShiftReach,
} from './api';

/**
 * השיחה בערוץ: ההודעות ותיבת הכתיבה (מסמך התכנון 9.5).
 *
 * רכיב משותף לשני הצדדים. באפליקציה הוא יושב ברוחב טלפון, ובקונסולה
 * ברוחב מסך בתוך פאנל — אותו קוד בדיוק. בלי זה למנהל היו שתי
 * תיבות כתיבה שונות, ואחת מהן הייתה מתיישנת.
 */
export function Conversation({
  channelId,
  listClassName,
}: {
  channelId: string;
  /** בקונסולה הרשימה מוגבלת בגובה וגוללת בתוך עצמה. */
  listClassName?: string;
}) {
  const { me } = useMe();
  const canAnnounce = usePerm('messages.announce');
  const canReadReports = usePerm('messages.read_reports');

  const channel = useChannel(channelId);
  const onShiftOnly = channel.data?.on_shift_only === true;
  const reach = useOnShiftReach(channelId, onShiftOnly);
  const messages = useMessages(channelId);
  const { send, ack, markRead } = useMessageMutations(channelId);
  useChannelRealtime(channelId);

  const [draft, setDraft] = useState('');
  const [requiresAck, setRequiresAck] = useState(false);
  const [pending, setPending] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const markedFor = useRef<string | null>(null);

  // כניסה לערוץ מסמנת נקרא, לא אישור
  useEffect(() => {
    if (!messages.data || markedFor.current === channelId) return;
    markedFor.current = channelId;
    markRead.mutate();
  }, [channelId, messages.data, markRead]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages.data]);

  if (!me) return null;

  const readOnly = channel.data?.is_announcement_only === true && !canAnnounce;
  const pinned = (messages.data ?? []).filter(
    (message) => message.requires_ack && !isAcked(message),
  );

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <div className="grid min-h-0 grid-rows-[1fr_auto]">
      <div className={cn('grid content-start gap-3 p-4', listClassName)}>
        {messages.isPending && (
          <>
            <Skeleton className="h-16 w-3/4" />
            <Skeleton className="h-16 w-2/3 justify-self-end" />
          </>
        )}

        {messages.isError && <ErrorState onRetry={() => void messages.refetch()} />}

        {messages.data?.length === 0 && (
          <EmptyState title="אין כאן עדיין הודעות" description="זה המקום להתחיל." />
        )}

        {pinned.map((message) => (
          <AckCard
            key={`pin-${message.id}`}
            message={message}
            onAck={() => run(() => ack.mutateAsync(message.id))}
          />
        ))}

        {(messages.data ?? []).map((message, index, list) => {
          const previous = list[index - 1];
          const showDay = !previous || !sameDay(previous.created_at, message.created_at);
          const showAuthor = !previous || previous.author_id !== message.author_id;

          return (
            <div key={message.id} className="grid gap-2">
              {showDay && (
                <p className="m-0 text-center text-label text-muted">
                  {dayLabel(message.created_at)}
                </p>
              )}
              <Bubble
                message={message}
                isMine={message.author_id === me.employeeId}
                showAuthor={showAuthor}
                canReadReports={canReadReports}
                onAck={() => run(() => ack.mutateAsync(message.id))}
              />
            </div>
          );
        })}

        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        <div ref={bottom} />
      </div>

      {readOnly ? (
        <p className="m-0 border-t border-line bg-cream p-4 text-center text-body-sm text-muted">
          זה ערוץ הודעות רשמיות. רק בעלי הרשאה כותבים בו.
        </p>
      ) : (
        <form
          className="grid gap-2 border-t border-line bg-surface p-3"
          onSubmit={(event) => {
            event.preventDefault();
            const body = draft.trim();
            if (!body && pending.length === 0) return;
            void run(async () => {
              await send.mutateAsync({
                body,
                authorId: me.employeeId,
                requiresAck,
                attachments: pending,
              });
              setDraft('');
              setRequiresAck(false);
              setPending([]);
            });
          }}
        >
          {canAnnounce && (
            <label className="flex min-h-11 items-center gap-2 text-body-sm">
              <input
                type="checkbox"
                checked={requiresAck}
                onChange={(event) => setRequiresAck(event.target.checked)}
                className="size-5"
              />
              דורש קריאה ואישור
            </label>
          )}

          {pending.length > 0 && (
            <ul className="grid list-none gap-1 p-0">
              {pending.map((file) => (
                <li
                  key={file.path}
                  className="flex items-center justify-between gap-2 rounded-sm border border-line px-2 py-1 text-body-sm"
                >
                  <span className="truncate">
                    {file.name} · {formatBytes(file.size)}
                  </span>
                  <button
                    type="button"
                    aria-label={`הסרת ${file.name}`}
                    onClick={() => setPending((prev) => prev.filter((f) => f.path !== file.path))}
                    className="cursor-pointer bg-transparent text-ink"
                  >
                    <X size={16} strokeWidth={1.75} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/*
            לכמה זה יגיע, לפני השליחה ולא אחריה. בערוץ כזה הנמענים
            נקבעים ברגע הלחיצה, ובלי המספר הזה אפשר לכתוב בשלוש
            לפנות בוקר לאפס אנשים ולחשוב שנמסר.
          */}
          {onShiftOnly && (
            <p
              className={`m-0 rounded-sm px-2 py-1 text-label ${
                reach.data === 0 ? 'bg-raised text-bad' : 'bg-cream text-ink'
              }`}
            >
              {reach.isPending
                ? 'בודק מי במשמרת…'
                : reach.data === 0
                  ? 'אף אחד לא במשמרת עכשיו. ההודעה תגיע אליך בלבד.'
                  : reach.data === 1
                    ? 'ההודעה תגיע לאדם אחד שנמצא עכשיו במשמרת.'
                    : `ההודעה תגיע ל-${reach.data} שנמצאים עכשיו במשמרת.`}
            </p>
          )}

          <div className="flex items-end gap-2">
            <label
              className="grid size-12 shrink-0 cursor-pointer place-items-center rounded-sm border border-muted text-ink"
              title="צירוף קובץ"
            >
              <Paperclip size={20} strokeWidth={1.75} aria-hidden="true" />
              <span className="sr-only">צירוף קובץ</span>
              <input
                type="file"
                multiple
                className="sr-only"
                onChange={(event) => {
                  const files = [...(event.target.files ?? [])];
                  event.target.value = '';
                  if (files.length === 0) return;
                  void run(async () => {
                    setIsUploading(true);
                    try {
                      for (const file of files) {
                        const uploaded = await uploadAttachment(channelId, file);
                        setPending((prev) => [...prev, uploaded]);
                      }
                    } finally {
                      setIsUploading(false);
                    }
                  });
                }}
              />
            </label>
            <label htmlFor="composer" className="sr-only">
              כתיבת הודעה
            </label>
            <textarea
              id="composer"
              rows={1}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="הודעה"
              className="min-h-11 flex-1 resize-none rounded-sm border border-muted bg-surface px-3 py-2 text-body"
            />
            <button
              type="submit"
              aria-label="שליחה"
              disabled={
                send.isPending || isUploading || (draft.trim() === '' && pending.length === 0)
              }
              className="grid size-12 shrink-0 place-items-center bg-charcoal text-on-charcoal disabled:opacity-45"
            >
              <Send size={20} strokeWidth={1.75} aria-hidden="true" className="rtl:-scale-x-100" />
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function AckCard({ message, onAck }: { message: ChannelMessage; onAck: () => void }) {
  return (
    <div className="grid gap-2 rounded-md border-s-4 border-charcoal bg-raised p-3">
      <StatusBadge tone="warn">דורש קריאה ואישור</StatusBadge>
      <p className="m-0 text-body">{message.body}</p>
      <div>
        <Button variant="primary" onClick={onAck}>
          קראתי ואני מאשר/ת
        </Button>
      </div>
    </div>
  );
}

function Bubble({
  message,
  isMine,
  showAuthor,
  canReadReports,
  onAck,
}: {
  message: ChannelMessage;
  isMine: boolean;
  showAuthor: boolean;
  canReadReports: boolean;
  onAck: () => void;
}) {
  const acked = isAcked(message);
  const author = message.employees
    ? `${message.employees.first_name} ${message.employees.last_name}`
    : '';

  return (
    <div
      className={cn('grid max-w-[85%] gap-1', isMine ? 'justify-self-end' : 'justify-self-start')}
    >
      {!isMine && showAuthor && author && <span className="text-label text-muted">{author}</span>}

      <div
        className={cn(
          'grid gap-2 rounded-lg p-3',
          isMine ? 'bg-olive text-on-olive' : 'border border-line bg-raised text-ink',
        )}
      >
        {message.body && <p className="m-0 whitespace-pre-wrap text-body">{message.body}</p>}

        {message.attachments?.length > 0 && <Attachments items={message.attachments} />}

        {/* הודעת "הכר את המוצר" נושאת כפתור לחידון (PRD 5.7.2) */}
        <SpotlightActions spotlights={message.product_spotlights} isMine={isMine} />

        {message.requires_ack &&
          !isMine &&
          (acked ? (
            <span className="flex items-center gap-1 text-body-sm text-good">
              <Check size={14} strokeWidth={1.75} aria-hidden="true" />
              אישרת
            </span>
          ) : (
            <Button variant="primary" onClick={onAck}>
              קראתי ואני מאשר/ת
            </Button>
          ))}
      </div>

      <span
        className={cn('flex items-center gap-1 text-label text-muted', isMine && 'justify-end')}
      >
        {formatTime(message.created_at)}
        {message.edited_at && <span>· נערך</span>}
        {isMine && <CheckCheck size={14} strokeWidth={1.75} aria-hidden="true" />}
        {message.requires_ack && canReadReports && (
          <Link to={`/admin/messages/reports/${message.id}`} className="text-muted underline">
            דוח אישורים
          </Link>
        )}
      </span>
    </div>
  );
}

/**
 * הכפתורים של הודעת "הכר את המוצר".
 *
 * הקישור מגיע מהשיוך בטבלה ולא מניתוח טקסט ההודעה: גוף ההודעה הוא
 * מה שההנהלה כתבה, והוא עלול להשתנות או להיערך.
 */
function SpotlightActions({
  spotlights,
  isMine,
}: {
  spotlights: ChannelMessage['product_spotlights'];
  isMine: boolean;
}) {
  const spotlight = spotlights?.[0];
  if (!spotlight) return null;

  const tone = isMine ? 'border border-on-olive text-on-olive' : 'bg-charcoal text-on-charcoal';

  return (
    <div className="flex flex-wrap gap-2">
      <Link
        to={`/products/${spotlight.product_id}`}
        className={cn(
          'inline-flex items-center gap-1 rounded-pill px-3 py-1.5 text-body-sm font-medium no-underline',
          isMine ? 'border border-on-olive text-on-olive' : 'border border-muted text-ink',
        )}
      >
        <Package size={16} strokeWidth={1.75} aria-hidden="true" />
        לדף המוצר
      </Link>

      {spotlight.quiz_id && (
        <Link
          to={`/learning/quiz/${spotlight.quiz_id}`}
          className={cn(
            'inline-flex items-center gap-1 rounded-pill px-3 py-1.5 text-body-sm font-medium no-underline',
            tone,
          )}
        >
          <GraduationCap size={16} strokeWidth={1.75} aria-hidden="true" />
          לחידון
        </Link>
      )}
    </div>
  );
}

/**
 * הבדלי פרטי, ולכן כל קובץ נפתח דרך Signed URL שנוצר בזמן הצפייה.
 * הקישורים תקפים לשעה, ונטענים פעם אחת לכל קבוצת קבצים.
 */
function Attachments({ items }: { items: Attachment[] }) {
  const paths = items.map((item) => item.path).join('|');
  const urls = useQuery({
    queryKey: ['attachment-urls', paths],
    queryFn: () => signedUrls(items.map((item) => item.path)),
    staleTime: 50 * 60 * 1000,
  });

  return (
    <ul className="grid list-none gap-2 p-0">
      {items.map((item) => {
        const url = urls.data?.[item.path];
        if (isImage(item.mime)) {
          return (
            <li key={item.path}>
              {url ? (
                <a href={url} target="_blank" rel="noreferrer">
                  <img
                    src={url}
                    alt={item.name}
                    loading="lazy"
                    className="max-h-64 w-full rounded-md object-cover"
                  />
                </a>
              ) : (
                <Skeleton className="h-40 w-full" />
              )}
            </li>
          );
        }
        return (
          <li key={item.path}>
            <a
              href={url ?? undefined}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-sm border border-line bg-surface p-2 text-body-sm text-ink no-underline"
            >
              <FileText size={20} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              <span className="shrink-0 text-muted">{formatBytes(item.size)}</span>
            </a>
          </li>
        );
      })}
    </ul>
  );
}

const isAcked = (message: ChannelMessage) =>
  message.message_reads.some((read) => read.acknowledged_at !== null);

function sameDay(a: string, b: string) {
  return formatDate(a) === formatDate(b);
}

function dayLabel(iso: string): string {
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (formatDate(iso) === formatDate(today)) return 'היום';
  if (formatDate(iso) === formatDate(yesterday)) return 'אתמול';
  return formatDate(iso);
}
